import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  Building2, 
  Menu, 
  X, 
  ChevronDown,
  BookOpen,
  FileText,
  HelpCircle
} from 'lucide-react';

/**
 * Reusable Header component with SEO-optimized navigation
 * Includes dropdown "Ressources" menu
 */
export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);

  const mainNavItems = [
    { label: 'Veille', href: '/veille-marches-publics' },
    { label: 'Trouver appels d\'offres', href: '/trouver-appels-offres' },
    { label: 'Analyse DCE', href: '/analyse-dce' },
    { label: 'Plateforme', href: '/plateforme-appels-offres' },
  ];

  const resourcesItems = [
    { label: 'Blog', href: '/blog', icon: BookOpen, desc: 'Articles et actualités' },
    { label: 'Répondre aux marchés publics', href: '/repondre-marches-publics', icon: FileText, desc: 'Guide complet' },
    { label: 'Guide appels d\'offres', href: '/guide-appels-offres', icon: HelpCircle, desc: 'Comprendre les DCE' },
  ];

  return (
    <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <span className="font-bold text-xl">Mon DCE Express</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {mainNavItems.map((item) => (
              <Link 
                key={item.href}
                to={item.href} 
                className="text-white/80 hover:text-white transition-colors"
              >
                {item.label}
              </Link>
            ))}
            
            {/* Resources Dropdown */}
            <div className="relative">
              <button
                onClick={() => setResourcesOpen(!resourcesOpen)}
                onBlur={() => setTimeout(() => setResourcesOpen(false), 200)}
                className="flex items-center gap-1 text-white/80 hover:text-white transition-colors"
              >
                Ressources
                <ChevronDown className={`w-4 h-4 transition-transform ${resourcesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {resourcesOpen && (
                <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50">
                  {resourcesItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                      onClick={() => setResourcesOpen(false)}
                    >
                      <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <item.icon className="w-4 h-4 text-violet-600" />
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{item.label}</div>
                        <div className="text-sm text-slate-500">{item.desc}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/register">
              <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 rounded-lg hover:bg-white/10"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-white/20">
            <div className="flex flex-col gap-2">
              <Link 
                to="/" 
                className="px-4 py-3 text-white/80 hover:bg-white/10 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Accueil
              </Link>
              
              {mainNavItems.map((item) => (
                <Link 
                  key={item.href}
                  to={item.href} 
                  className="px-4 py-3 text-white/80 hover:bg-white/10 rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              
              {/* Mobile Resources Section */}
              <div className="px-4 py-2 text-sm font-semibold text-white/50 uppercase tracking-wide">
                Ressources
              </div>
              {resourcesItems.map((item) => (
                <Link 
                  key={item.href}
                  to={item.href} 
                  className="px-4 py-3 text-white/80 hover:bg-white/10 rounded-lg flex items-center gap-3"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              ))}
              
              <div className="border-t border-white/20 mt-2 pt-4 flex flex-col gap-2">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full border-white/30 text-white hover:bg-white/10">
                    Connexion
                  </Button>
                </Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-white text-violet-900 hover:bg-slate-100">
                    S'inscrire
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
